import React, { useEffect, useState, useMemo } from "react";
import { api, assetUrl } from "@/lib/api";
import { SectionTitle } from "@/components/ArticleCards";
import { Mail } from "lucide-react";
import { BOARD_ROLES } from "@/lib/constants";
import { useI18n } from "@/lib/i18n";

export default function EditorialBoard() {
  const { t } = useI18n();
  const [members, setMembers] = useState([]);
  useEffect(() => { api.get("/editorial-board").then((r) => setMembers(r.data || [])).catch(() => {}); }, []);

  const grouped = useMemo(() => {
    const map = {};
    BOARD_ROLES.forEach((r) => (map[r] = []));
    members.forEach((m) => {
      const k = m.role || "Reviewer";
      if (!map[k]) map[k] = [];
      map[k].push(m);
    });
    return Object.entries(map).filter(([_, v]) => v.length > 0);
  }, [members]);

  return (
    <div className="py-16 md:py-24" data-testid="editorial-board-page">
      <div className="max-w-5xl mx-auto px-6">
        <SectionTitle subtitle={t("board.subtitle")}>
          {t("board.title")}
        </SectionTitle>
        {members.length === 0 ? (
          <div className="text-center py-20 font-serif text-[#001F3F]/60 border border-dashed border-gray-200">
            {t("board.empty")}
          </div>
        ) : (
          grouped.map(([role, list]) => (
            <section key={role} className="mb-14">
              <h2 className="font-serif text-2xl text-[#001F3F] mb-2">{role}</h2>
              <div className="w-10 h-[2px] bg-[#2BA6A6] mb-6"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {list.map((m) => (
                  <div key={m.id} className="flex gap-5 p-5 border border-gray-200 bg-white">
                    {m.photo_url ? (
                      <img src={assetUrl(m.photo_url)} alt={m.full_name} className="w-20 h-20 rounded-full object-cover border border-gray-100 shrink-0" />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-[#F5F7FA] flex items-center justify-center text-[#001F3F] font-serif text-xl shrink-0 border border-gray-100">
                        {(m.full_name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="font-serif text-lg text-[#001F3F] leading-snug">{m.full_name}</div>
                      {m.specialty && <div className="text-xs font-sans uppercase tracking-wider text-[#2BA6A6] mt-1">{m.specialty}</div>}
                      {m.institution && <div className="text-sm font-sans text-[#001F3F]/80 mt-1">{m.institution}</div>}
                      {(m.city || m.country) && <div className="text-xs font-sans text-gray-500">{[m.city, m.country].filter(Boolean).join(", ")}</div>}
                      {m.bio && <p className="text-xs font-sans text-gray-600 mt-2 leading-relaxed">{m.bio}</p>}
                      {m.email && (
                        <a href={`mailto:${m.email}`} className="mt-2 inline-flex items-center gap-1 text-xs text-[#2BA6A6] hover:text-[#001F3F]">
                          <Mail className="w-3 h-3" /> {m.email}
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))
        )}
      </div>
    </div>
  );
}
